import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@r/utils/utils.ts';
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { ArrowRight } from 'lucide-react';

const menuItemVariants = cva(
  'menu-item flex space-between bg-transparent items-center whitespace-nowrap font-light cursor-pointer disabled:opacity-30 disabled:pointer-events-none hover:duration-300 ease-in-out',
  {
    variants: {
      variant: {
        default: 'rounded-lg hover:bg-muted data-[active]:bg-muted',
        mobile:
          'flex justify-between border-b border-foreground text-foreground hover:bg-muted data-[active]:bg-muted',
        sub: 'rounded-md text-muted-foreground hover:bg-background data-[active]:bg-background'
      },
      size: {
        default: 'text-[1rem] h-9 w-full px-[0.5rem]',
        sub: 'text-[0.8rem] h-6 w-full ps-[2.2rem] pe-2 my-[0.3rem]'
      }
    },
    defaultVariants: {
      variant: 'default',
      size: 'default'
    }
  }
);

export interface MenuItemProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof menuItemVariants> {
  className?: string;
  variant?: 'default' | 'mobile' | 'sub';
  size?: 'default' | 'sub';
  label?: string | null;
  disabled?: boolean;
  backgroundColor?: string;
  color?: string;
  onClick?: () => void;
  asChild?: boolean;
  children?: ReactNode;
}

const MenuItem = forwardRef<HTMLButtonElement, MenuItemProps>(
  (
    {
      className,
      variant,
      disabled,
      size,
      backgroundColor,
      onClick,
      color,
      asChild = false,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button';

    if (variant === 'sub') {
      size = 'sub';
    }

    return (
      <Comp
        disabled={disabled}
        onClick={onClick}
        className={cn(menuItemVariants({ variant, size, className }))}
        style={{ backgroundColor, color }}
        ref={ref}
        {...props}
      >
        <span style={{ display: 'flex', whiteSpace: 'nowrap' }}>{props.children}</span>

        {variant === 'mobile' && <ArrowRight className="ml-1 mobile-arrow-icon" />}
      </Comp>
    );
  }
);

MenuItem.displayName = 'MenuItem';

export { MenuItem };
